import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo } from 'react'

import { ReactComponent as ChevronLeft } from 'assets/icons/chevron-left.svg'
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close-icon.svg'
import { AccordionHeader } from 'components/accordionHeader/AccordionHeader'
import { Accordion } from 'components/common/Accordion/Accordion'
import { Button } from 'components/common/button/Button'
import { FullScreenModal } from 'components/common/FullScreenModal/FullScreenModal'
import { Icon } from 'components/common/Icon/Icon'
import { Typography } from 'components/common/Typography/Typography'
import { AnalyticsActionType } from 'types/common/analytics.type'

import styles from './InnovationModal.module.scss'
import { InnovationModalData } from './InnovationModalData'
import Timeline, { TimelineVariant } from './Timeline/Timeline'
import ClockCircle from '../../../assets/images/innovation/clock_circle.svg'
import Star from '../../../assets/images/innovation/star.svg'
import { EVENTS, trackAnalytics } from '../../../utils/analytics'
import Bubble from '../../common/bubble/Bubble'
import { Flex } from '../../common/flex/Flex'
import Dot from '../assets/Dot'
import { ObjectType } from '../types/types'

interface InnovationModalProps {
  isModalOpened: boolean
  setIsModalOpened: (isOpen: boolean) => void
  setInnovationKey: (key: string) => void
  innovationKey?: string
  innovationSubheadRef?: React.RefObject<HTMLDivElement>
}

declare global {
  interface Window {
    Intercom: (...args: any[]) => void
  }
}

function replaceBrWithSpace(inputString: string) {
  return inputString.replace(/<br\s*\/?>/gi, ' ')
}

const InnovationModal = ({
  isModalOpened,
  setIsModalOpened,
  setInnovationKey,
  innovationKey,
  innovationSubheadRef,
}: InnovationModalProps) => {
  const [modalData, setModalData] = React.useState<ObjectType | null>(null)
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const loopPhases = useMemo(() => Object.keys(InnovationModalData[0]), [])
  const modalBodyRef = React.useRef<HTMLDivElement>(null)
  const modalContentRef = React.useRef<HTMLDivElement>(null)
  const [expandedAccordion, setExpandedAccordion] = React.useState<number[]>([])

  useEffect(() => {
    isModalOpened
      ? window.Intercom('update', { hide_default_launcher: true })
      : window.Intercom('update', { hide_default_launcher: false })
  }, [isModalOpened])

  useEffect(() => {
    let data: any
    if (innovationKey !== undefined) {
      data = InnovationModalData[0][innovationKey]
    }
    const loopPhaseIndex = innovationKey ? loopPhases.indexOf(innovationKey.toString()) : -1
    setCurrentIndex(loopPhaseIndex)

    if (!innovationKey) {
      setIsModalOpened(false)
      return
    }

    if (data) setModalData(data)
  }, [innovationKey, setIsModalOpened, loopPhases])

  useEffect(() => {
    if (isModalOpened && modalContentRef.current) {
      modalContentRef.current.scrollIntoView()
    }
  }, [isModalOpened])

  const sendAnalyticsEvent = (event: string) => {
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: event,
    })
  }

  const handleCloseModal = () => {
    setIsModalOpened(false)
    setInnovationKey('')
    setExpandedAccordion([])
    if (innovationSubheadRef && innovationSubheadRef.current)
      innovationSubheadRef?.current.scrollIntoView({ behavior: 'smooth' })
  }

  const handleArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      let newIndex

      if (direction === 'left') {
        if (currentIndex > 0) {
          newIndex = currentIndex - 1
        }
      } else if (currentIndex < loopPhases.length - 1) {
        newIndex = currentIndex + 1
      }

      if (newIndex !== undefined) {
        setCurrentIndex(newIndex)
        setExpandedAccordion([])
        const newLoopPhase = loopPhases[newIndex]
        setModalData(InnovationModalData[0][newLoopPhase])
        sendAnalyticsEvent(InnovationModalData[0][newLoopPhase].mktEvent)
      }
    },
    [currentIndex, loopPhases, setCurrentIndex, setModalData],
  )

  if (!modalData) {
    return null
  }

  function getMarginLeft() {
    const screenWidth = window.innerWidth

    if (screenWidth <= 1380) {
      return currentIndex === 3 ? '100px' : '40px'
    }
  }

  const { title, subtitle, bubbleText, backgroundColor, titleColor, nextPhase, timeline, dropdown } = modalData
  const sideTitle = replaceBrWithSpace(title)
  return (
    <FullScreenModal open={isModalOpened} className={styles.modalContainer}>
      <div slot="body" ref={modalBodyRef} className="relative h-full">
        <div className={styles.closeButton}>
          <Icon icon={CloseIcon} size={20} color="#000000" className={styles.closeIcon} onClick={handleCloseModal} />
        </div>
        <div ref={modalContentRef} className="relative flex flex-row items-stretch h-full overflow-y-auto">
          <div className="w-[120px]" style={{ backgroundColor }} />
          <div className="flex-1" />
          <div className="fixed h-full flex flex-row items-stretch overflow-y-auto w-full">
            <div className="w-[120px] bg-transparent">
              <div className={styles.sideTextContainer}>
                <p className={styles.verticalText} style={{ WebkitTextStroke: `2px ${titleColor}` }}>
                  {sideTitle}
                </p>
              </div>
            </div>
            <div
              className={clsx(
                title === 'Simplify for Growth' ? styles.comingSoonContainer : '',
                'pt-[114px] flex-1 h-full',
              )}
            >
              <Flex
                direction="column"
                className={clsx(styles.headerContainer, title === 'Simplify for Growth' ? styles.comingSoonTitle : '')}
              >
                <Flex justify="between" align="start" className={styles.headerTopContainer}>
                  <Flex
                    gap={16}
                    className={styles.titleWrapper}
                    style={{ alignItems: title === 'Market<br>Execution' ? 'center' : 'end' }}
                  >
                    {title !== 'Idea' && (
                      <Icon
                        icon={ChevronLeft}
                        size={32}
                        color="#000000"
                        className={styles.arrow}
                        onClick={() => handleArrowClick('left')}
                      />
                    )}
                    <h3 className="text-[36px] leading-[48px] font-bold text-center">
                      <div dangerouslySetInnerHTML={{ __html: title }} />
                    </h3>
                    {title !== 'Simplify for Growth' && (
                      <Icon
                        icon={ChevronRight}
                        size={32}
                        color="#000000"
                        className={styles.arrow}
                        onClick={() => handleArrowClick('right')}
                      />
                    )}
                  </Flex>
                  {title !== 'Simplify for Growth' && (
                    <div className={styles.textWrapper} style={{ marginLeft: getMarginLeft() }}>
                      <Typography type="l-midi" tag="p" className={styles.modalSubtitle}>
                        <Bubble color="#E4FF3C" text={bubbleText} direction="down" className={styles.bubble} />
                        {subtitle}
                      </Typography>
                    </div>
                  )}
                </Flex>
                {title !== 'Simplify for Growth' && (
                  <Flex gap={15} direction="column" align="start" className={styles.timelineContainer}>
                    <Flex justify="between" style={{ width: '96.5%' }}>
                      <p className={styles.timelineStart}>Start</p>
                      <p className={styles.timelineStart}>{nextPhase}</p>
                    </Flex>
                    <div className={styles.timelineWrap}>
                      <Flex className={styles.timelineWrapper}>
                        <Timeline
                          color={backgroundColor}
                          variant={title === 'Simplify for Growth' ? TimelineVariant.LINE : TimelineVariant.ARROW_RIGHT}
                        />
                      </Flex>
                      <Flex className={clsx(styles.timeline, styles.timelineTextWrapper)}>
                        {Object.values(timeline || {}).map((item: any, index: number) => {
                          return (
                            <Flex direction="column" gap={28} className={styles.timelineText} key={item.title}>
                              <Dot containerClassName={styles.dotContainer} fill={titleColor} className={styles.dot} />
                              <Typography type="m-strong" tag="p" className={styles.timelineText}>
                                <Flex direction="row" gap={5}>
                                  <div>{index + 1 + '. '}</div>
                                  <div dangerouslySetInnerHTML={{ __html: item.title }} />
                                </Flex>
                              </Typography>
                            </Flex>
                          )
                        })}
                        <div
                          className={styles.empty}
                          style={{ display: title === 'Simplify for Growth' ? 'none' : 'block' }}
                        >
                          <Dot
                            style={{ justifyContent: 'end' }}
                            containerClassName={styles.dotContainer}
                            fill={titleColor}
                            className={styles.dot}
                          />
                        </div>
                      </Flex>
                    </div>
                  </Flex>
                )}
              </Flex>
              {title !== 'Simplify for Growth' && (
                <div className={styles.middleTitleContainer}>
                  <Typography type="3xl-heading" tag="p" className={clsx(styles.middleTitle, styles.highlight)}>
                    Key Tasks
                  </Typography>
                  <div className={styles.starWrapper}>
                    <img src={Star} alt="" />
                  </div>
                </div>
              )}
              {title !== 'Simplify for Growth' ? (
                <div className={styles.accordionContainer}>
                  {Object.values(dropdown || {}).map((item: any, index: number) => {
                    return (
                      <Accordion
                        expanded={expandedAccordion.includes(index)}
                        onChange={() => {
                          if (expandedAccordion.includes(index)) {
                            setExpandedAccordion(expandedAccordion.filter(i => i !== index))
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: EVENTS.INNOVATION_VIEWMODAL.MINIMIZE(
                                replaceBrWithSpace(title).toLowerCase(),
                                index + 1,
                              ),
                            })
                          } else {
                            setExpandedAccordion([...expandedAccordion, index])
                            trackAnalytics({
                              type: AnalyticsActionType.action,
                              payload: EVENTS.INNOVATION_VIEWMODAL.EXPAND(
                                replaceBrWithSpace(title).toLowerCase(),
                                index + 1,
                              ),
                            })
                          }
                        }}
                        className={styles.accordion}
                        key={item.title}
                      >
                        <AccordionHeader slot="header" title={item.title} />
                        <Flex gap={52} direction="column" className={styles.textContainer}>
                          <div className={styles.textWrapper}>
                            <Typography type="m-body" tag="p" className={styles.accordionText}>
                              {item.text}
                            </Typography>
                          </div>
                          {item.table && (
                            <table
                              style={{
                                overflow: 'hidden',
                                borderCollapse: 'separate',
                                border: '2px solid',
                                borderColor: backgroundColor,
                                borderRadius: '16px',
                                borderSpacing: 0,
                              }}
                            >
                              <thead
                                style={{
                                  backgroundColor: backgroundColor,
                                }}
                              >
                                <tr>
                                  {Object.keys(item.table).map(columnKey => (
                                    <th className={styles.tableHead} key={columnKey}>
                                      <Typography type="m-strong" tag="p" className={styles.tableHeadText}>
                                        {item.table[columnKey].title}
                                      </Typography>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className={styles.tableBody}>
                                <tr className={styles.tableRow}>
                                  {Object.entries(item.table).map(([columnKey, columnValue], index: number) => (
                                    <td
                                      className={styles.tableCell}
                                      key={columnKey}
                                      style={
                                        index === 1
                                          ? {
                                              borderLeft: '4px solid',
                                              borderRight: '4px solid',
                                              borderColor: backgroundColor,
                                            }
                                          : {}
                                      }
                                    >
                                      <ul>
                                        {typeof columnValue === 'object' &&
                                          columnValue !== null &&
                                          Object.entries(columnValue).map(([rowKey, rowValue]) => {
                                            if (rowKey.startsWith('row')) {
                                              return (
                                                <li className={styles.cellText} key={rowKey}>
                                                  <Typography type="m-body" tag="p" className={styles.tableCellText}>
                                                    <div dangerouslySetInnerHTML={{ __html: rowValue }} />
                                                  </Typography>
                                                </li>
                                              )
                                            }
                                            return null
                                          })}
                                      </ul>
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </Flex>
                      </Accordion>
                    )
                  })}
                </div>
              ) : (
                <Flex direction="column" gap={24} align="center" className={styles.comingSoonContainer}>
                  <div className={styles.clockImgContainer}>
                    <img
                      width={120}
                      height={120}
                      src={require('../../../assets/images/innovation/clock.gif')}
                      alt="Coming Soon"
                      className={styles.clock}
                    />
                    <img src={ClockCircle} alt="" className={styles.clockCircle} />
                  </div>
                  <Typography tag="p" type="m-body" className={styles.comingSoon}>
                    Coming Soon
                  </Typography>
                </Flex>
              )}
              <div className={clsx(styles.buttonContainer, styles.comingSoonButton)}>
                <Button
                  variant="primary"
                  size="fit"
                  onClick={() => {
                    handleCloseModal()
                    trackAnalytics({
                      type: AnalyticsActionType.action,
                      payload: EVENTS.INNOVATION_VIEWMODAL_SEEFULL(title.toLowerCase()),
                    })
                  }}
                  className={styles.button}
                  text="See Full Innovation Process"
                />
              </div>
            </div>
          </div>
          {/* */}
        </div>
      </div>
    </FullScreenModal>
  )
}

export default InnovationModal
